import { useRef, useEffect, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Eraser, PenLine, Check } from 'lucide-react';

// Digital signature pad — draw with finger / stylus (mobile & tablet) or mouse (desktop).
export const SignaturePad = ({ open, onOpenChange, onSave, saving = false }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [hasInk, setHasInk] = useState(false);

  const prepare = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 2.4;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    setHasInk(false);
  }, []);

  useEffect(() => {
    if (open) {
      // allow the dialog to size, then init canvas backing store
      const t = setTimeout(prepare, 60);
      return () => clearTimeout(t);
    }
  }, [open, prepare]);

  const pos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    last.current = pos(e);
    canvasRef.current.setPointerCapture?.(e.pointerId);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (!hasInk) setHasInk(true);
  };
  const end = (e) => { drawing.current = false; };

  const handleClear = () => prepare();

  const handleSave = async () => {
    const canvas = canvasRef.current;
    const blob = await new Promise((r) => canvas.toBlob(r, 'image/png'));
    if (blob) onSave(blob);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="signature-pad-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PenLine className="h-4 w-4" /> Guest Signature</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500 -mt-1">Sign inside the box using your finger, stylus, or mouse.</p>
        <div className="rounded-xl border-2 border-dashed border-slate-300 bg-white overflow-hidden">
          <canvas
            ref={canvasRef}
            width={640}
            height={240}
            className="w-full h-[200px] touch-none cursor-crosshair"
            onPointerDown={start}
            onPointerMove={move}
            onPointerUp={end}
            onPointerLeave={end}
            data-testid="signature-canvas"
          />
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={handleClear} data-testid="signature-clear-btn">
            <Eraser className="h-4 w-4 mr-1" /> Clear
          </Button>
          <div className="flex-1" />
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="button" onClick={handleSave} disabled={!hasInk || saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="signature-save-btn">
            <Check className="h-4 w-4 mr-1" /> {saving ? 'Saving…' : 'Save Signature'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SignaturePad;
